import cx from 'classnames';
import {CSSProperties, useContext, useMemo} from 'react';
import Skeleton from 'react-loading-skeleton';
import {FabricTemplate} from '~/common/fabric/types';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {BottomSheetTabContext} from '~/QuickEcards/context/BottomSheetTabContext';
import {useAppSelector} from '~/QuickEcards/store';
import {selectSelectedAsset} from '~/QuickEcards/store/reducers/ecard';
import {Asset} from '~/QuickEcards/types/ecard';
import {OverlayIcon, Tab} from '~/QuickEcards/types/misc';
import {Button} from '~library/atoms/Button';
import {Video} from '~library/atoms/Video';
import {VideoAlphaChannel} from '~library/atoms/VideoAlphaChannel';
import 'react-loading-skeleton/dist/skeleton.css';
import './OptionsGrid.sass';

const NoneCell = ({
  onClick,
  type,
  selected = false,
  cellStyle,
}: {
  onClick: () => void;
  type: Tab;
  selected?: boolean;
  cellStyle?: CSSProperties;
}) => (
  <div
    className={cx('options-contents__item__parent', type, {
      'cell-selected': selected,
    })}
    style={cellStyle}
  >
    <Button
      variant="unstyled-button"
      onClick={() => onClick()}
      className="options-contents__item none"
      data-qa-id={`${type}-none`}
    >
      None
    </Button>
  </div>
);

const Cell = ({
  item,
  onAssetClick,
  type,
  selected = false,
  cellStyle,
}: {
  item: Asset | OverlayIcon | FabricTemplate;
  onAssetClick: (id: string) => void;
  type: Tab;
  selected?: boolean;
  cellStyle?: CSSProperties;
}) => {
  const Graphic = useMemo(() => {
    if ('iconVp9' in item) {
      return (
        <VideoAlphaChannel
          style={{objectFit: 'cover', width: '100%', height: '100%'}}
          srcVp9={item?.iconVp9}
          srcH265={item?.iconH265}
          inViewAutoPlay
        />
      );
    }
    const src =
      'assets' in item
        ? (item.assets.default['500x680_optimized'] ?? item.assets.default['500x680'] ?? '')
        : item.media[0].url;
    if (src.endsWith('.mp4')) {
      return <Video src={src} className="options-contents__item--img" />;
    }
    return <img src={src} className="options-contents__item--img" alt={item.display_name} />;
  }, [item]);

  return (
    <div
      className={cx('options-contents__item__parent', type, {
        'cell-selected': selected,
      })}
      style={cellStyle}
    >
      <Button
        variant="unstyled-button"
        onClick={() => onAssetClick(item.id)}
        className="options-contents__item"
        data-qa-id={`${type}-${item.id}`}
      >
        {Graphic}
      </Button>
    </div>
  );
};
export const OptionsGrid = ({
  assets,
  onAssetClick,
  className,
  tab = 'design',
  cellStyle,
}: {
  assets: OverlayIcon[] | FabricTemplate[];
  onAssetClick: (id: string) => void;
  className?: string;
  tab?: Tab;
  cellStyle?: CSSProperties;
}) => {
  const isDesktop = useIsLargishAndUp();
  const {snap} = useContext(BottomSheetTabContext);
  const selectedAsset = useAppSelector((state) => selectSelectedAsset(state, tab));
  const isRevealOrEffect = tab === 'reveals' || tab === 'effects';

  const dynamicHeight = isDesktop
    ? '100%'
    : ((typeof snap === 'string' ? snap : `calc(100dvh - (${snap! * 100}px + 50px))`) ?? '100px');

  const icons = useMemo(() => {
    if (!tab) return [];
    return assets;
  }, [tab, assets]);

  return (
    <div className="options-contents__grid-wrapper">
      <div
        className={cx('options-contents__grid', tab, className)}
        style={{maxHeight: dynamicHeight, gridAutoRows: 'min-content'}}
      >
        {isRevealOrEffect && (
          <NoneCell
            key="none"
            type={tab}
            onClick={() => onAssetClick('')}
            selected={!selectedAsset || !icons.some((i) => i.id === selectedAsset)}
            cellStyle={cellStyle}
          />
        )}
        {icons.length > 0
          ? icons.map((ele) => (
              <Cell
                key={ele.id}
                type={tab}
                item={ele}
                onAssetClick={onAssetClick}
                selected={ele.id === selectedAsset}
                cellStyle={cellStyle}
              />
            ))
          : Array.from<number>({length: 75})
              .fill(0)
              .map(() => (
                <Skeleton
                  key={crypto.randomUUID()}
                  width={70}
                  height={90}
                  className="options-contents__item__parent"
                />
              ))}
      </div>
    </div>
  );
};
